import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Trophy, Users, Building2, Globe, Loader2, Lock, EyeOff, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METRICS = ["optimizations", "applications", "shortlisted", "interviews", "offers"];
const LABELS = {
  optimizations: "Optimizations", applications: "Applications", shortlisted: "Shortlisted (RTR)",
  interviews: "Interviews", offers: "Offers (Job won)",
};
const PROFILE_KEY = {
  optimizations: "show_optimizations", applications: "show_applications", shortlisted: "show_shortlists",
  interviews: "show_interviews", offers: "show_offers",
};

const Toggle = ({ on, onClick, disabled, testid }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    style={{ width: 44, height: 25, borderRadius: 999, border: "none", cursor: disabled ? "not-allowed" : "pointer",
      flexShrink: 0, opacity: disabled ? 0.4 : 1, background: on ? "#6366f1" : "var(--border-main)",
      position: "relative", transition: "background 0.15s" }}>
    <span style={{ position: "absolute", top: 3, left: on ? 22 : 3, width: 19, height: 19, borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
  </button>
);

const MetricToggles = ({ prefs, onToggle, disabled, testidPrefix }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 4 }}>
    {METRICS.map((m) => (
      <div key={m} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.55rem 0.2rem" }}>
        <span style={{ fontSize: "0.88rem", color: "var(--text-secondary)" }}>{LABELS[m]}</span>
        <Toggle on={!!prefs?.[m]} disabled={disabled} onClick={() => onToggle(m)} testid={`${testidPrefix}-${m}`} />
      </div>
    ))}
  </div>
);

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <div className="card" data-testid={testid} style={{ padding: "1.4rem", marginBottom: "1.2rem" }}>
    <h3 style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}><Icon size={19} /> {title}</h3>
    <p style={{ fontSize: "0.84rem", color: "var(--text-secondary)", marginTop: 0, marginBottom: "1rem" }}>{desc}</p>
    {children}
  </div>
);

export default function PrivacyCenterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [squads, setSquads] = useState([]);
  const [agencies, setAgencies] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/social/privacy-overview`);
      const d = r.data;
      setProfile(d.profile_privacy || {});
      setDefaults(d.default_share_prefs || {});
      setSquads(d.squads || []);
      setAgencies((d.agencies || []).filter((a) => a.role === "candidate" && a.share_prefs));
    } catch (e) {
      toast.error("Couldn't load your privacy settings.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Global / public profile ──
  const togglePublic = async () => {
    const next = { ...profile, public_profile: !profile.public_profile };
    setProfile(next);
    try { await axios.put(`${API}/social/profile`, { privacy: next }); toast.success(next.public_profile ? "Public profile on" : "Public profile hidden"); }
    catch { setProfile(profile); toast.error("Couldn't save"); }
  };
  const toggleProfileMetric = async (m) => {
    const key = PROFILE_KEY[m];
    const next = { ...profile, [key]: !profile[key] };
    setProfile(next);
    try { await axios.put(`${API}/social/profile`, { privacy: next }); }
    catch { setProfile(profile); toast.error("Couldn't save"); }
  };

  // ── Squad defaults ──
  const toggleDefault = async (m) => {
    const next = { ...defaults, [m]: !defaults[m] };
    setDefaults(next);
    try { await axios.put(`${API}/social/default-share-prefs`, { prefs: next }); }
    catch { setDefaults(defaults); toast.error("Couldn't save"); }
  };

  // ── Per-squad ──
  const toggleSquad = async (sid, m) => {
    const prev = squads;
    const next = squads.map((s) => s.id === sid ? { ...s, share_prefs: { ...s.share_prefs, [m]: !s.share_prefs[m] } } : s);
    setSquads(next);
    const sp = next.find((s) => s.id === sid).share_prefs;
    try { await axios.put(`${API}/squads/${sid}/my-prefs`, { prefs: sp }); }
    catch { setSquads(prev); toast.error("Couldn't save"); }
  };

  // ── Per-agency ──
  const toggleAgency = async (aid, m) => {
    const prev = agencies;
    const next = agencies.map((a) => a.id === aid ? { ...a, share_prefs: { ...a.share_prefs, [m]: !a.share_prefs[m] } } : a);
    setAgencies(next);
    const sp = next.find((a) => a.id === aid).share_prefs;
    try { await axios.put(`${API}/agencies/${aid}/my-share`, { share_prefs: sp }); }
    catch { setAgencies(prev); toast.error("Couldn't save"); }
  };

  const profileMetricPrefs = profile ? Object.fromEntries(METRICS.map((m) => [m, !!profile[PROFILE_KEY[m]]])) : {};

  if (loading) {
    return <div className="page-container" style={{ textAlign: "center", padding: "4rem" }} data-testid="privacy-loading"><Loader2 size={24} className="spin" /></div>;
  }

  return (
    <div className="page-container fade-in" data-testid="privacy-center-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: 10 }}><ShieldCheck size={24} /> Privacy</h1>
          <p style={{ color: "var(--text-secondary)" }}>One place to control exactly which of your stats are shared — on the global leaderboard, in each squad, and with each agency.</p>
        </div>
      </div>

      {/* GLOBAL */}
      <Section icon={Globe} title="Global leaderboard & public profile" testid="privacy-global-section"
        desc="Your public profile and the global leaderboard. When your profile is hidden, you don't appear publicly at all.">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.6rem 0", borderBottom: "1px solid var(--border-main)", marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: "0.92rem" }}>Public profile {profile?.public_profile ? "" : "(hidden)"}</div>
            <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>Master switch — off hides you from search, profile pages and all global leaderboards.</div>
          </div>
          <Toggle on={!!profile?.public_profile} onClick={togglePublic} testid="privacy-public-toggle" />
        </div>
        <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
          <Trophy size={14} /> Metrics shown on the global leaderboard & your profile
        </div>
        <MetricToggles prefs={profileMetricPrefs} onToggle={toggleProfileMetric} disabled={!profile?.public_profile} testidPrefix="privacy-global" />
      </Section>

      {/* SQUAD DEFAULTS */}
      <Section icon={Users} title="Squads — default sharing" testid="privacy-squad-defaults-section"
        desc="Applied automatically when you join a NEW squad. You can still fine-tune each squad below.">
        <MetricToggles prefs={defaults} onToggle={toggleDefault} testidPrefix="privacy-default" />
      </Section>

      {/* PER-SQUAD */}
      <Section icon={Users} title="Squads — per squad" testid="privacy-squads-section"
        desc="Choose exactly what you share inside each squad you're in. Hidden metrics show as '—' on that squad's leaderboard.">
        {squads.length === 0 ? (
          <div data-testid="privacy-squads-empty" style={{ color: "var(--text-muted)", fontSize: "0.86rem", textAlign: "center", padding: "1rem" }}>
            You haven't joined any squads yet. <button className="link-btn" onClick={() => navigate("/app/squads")} style={{ color: "#6366f1", background: "none", border: "none", cursor: "pointer" }}>Browse squads <ChevronRight size={12} /></button>
          </div>
        ) : squads.map((s) => (
          <div key={s.id} data-testid={`privacy-squad-${s.id}`} style={{ padding: "0.9rem 0", borderTop: "1px solid var(--border-main)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontWeight: 600 }}>{s.name} <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>· {s.shared_count}/5 shared</span></div>
            </div>
            <MetricToggles prefs={s.share_prefs} onToggle={(m) => toggleSquad(s.id, m)} testidPrefix={`privacy-squad-${s.id}`} />
          </div>
        ))}
      </Section>

      {/* PER-AGENCY */}
      <Section icon={Building2} title="Agencies — what you share" testid="privacy-agencies-section"
        desc="Each agency you've joined sees only the metrics you allow here. Turn all off to fully revoke sharing (you stay a member).">
        {agencies.length === 0 ? (
          <div data-testid="privacy-agencies-empty" style={{ color: "var(--text-muted)", fontSize: "0.86rem", textAlign: "center", padding: "1rem" }}>
            You haven't joined any agencies as a candidate.
          </div>
        ) : agencies.map((a) => {
          const sharedCount = METRICS.filter((m) => a.share_prefs?.[m]).length;
          return (
            <div key={a.id} data-testid={`privacy-agency-${a.id}`} style={{ padding: "0.9rem 0", borderTop: "1px solid var(--border-main)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ fontWeight: 600 }}>{a.name} <span style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>@{a.handle} · {sharedCount}/5 shared</span></div>
                {sharedCount === 0 && <span style={{ fontSize: "0.72rem", color: "#ef4444", display: "flex", alignItems: "center", gap: 4 }}><Lock size={12} /> Sharing off</span>}
              </div>
              <MetricToggles prefs={a.share_prefs} onToggle={(m) => toggleAgency(a.id, m)} testidPrefix={`privacy-agency-${a.id}`} />
            </div>
          );
        })}
      </Section>

      <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)", fontSize: "0.8rem", marginBottom: "2rem" }}>
        <EyeOff size={14} /> Changes save automatically. Hidden metrics are excluded from leaderboards, analytics totals and reports.
      </div>
    </div>
  );
}
